import * as amplitude from '@amplitude/analytics-browser';

// Initialize Amplitude
export const initializeAnalytics = () => {
  const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY || '';
  
  if (!apiKey) {
    console.warn('⚠️ Amplitude API key not found. Analytics will be disabled.');
    return;
  }

  amplitude.init(apiKey, {
    // Use your user ID if available for user tracking
    defaultTracking: {
      sessions: true,
      pageViews: true,
      formInteractions: false,
    },
  });
  
  console.log('📊 Amplitude initialized for web platform');
};

// User identification for authenticated users
export const identifyUser = (userId: string, userProperties?: Record<string, any>) => {
  amplitude.setUserId(userId);
  
  if (userProperties) {
    const identifyEvent = new amplitude.Identify();
    Object.entries(userProperties).forEach(([key, value]) => {
      identifyEvent.set(key, value);
    });
    amplitude.identify(identifyEvent);
  }
};

// Set user properties
export const setUserProperties = (properties: Record<string, any>) => {
  const identifyEvent = new amplitude.Identify();
  
  Object.entries(properties).forEach(([key, value]) => {
    identifyEvent.set(key, value);
  });
  
  amplitude.identify(identifyEvent);
};

// Configuration for event names to match iOS exactly
const EVENT_NAMES = {
  // Authentication Events - iOS Aligned
  CREATE_ACCOUNT_DID_SUCCEED: process.env.REACT_APP_SIGNUP_EVENT_NAME || 'createAccountDidSucceed',
  CREATE_ACCOUNT_DID_FAIL: process.env.REACT_APP_SIGNUP_FAIL_EVENT_NAME || 'createAccountDidFail',
  CREATE_ACCOUNT_WAS_ATTEMPTED: process.env.REACT_APP_SIGNUP_ATTEMPT_EVENT_NAME || 'createAccountWasAttempted',
  
  ENTER_PASSWORD_DID_SUCCEED: process.env.REACT_APP_LOGIN_EVENT_NAME || 'enterPasswordDidSucceed',
  ENTER_PASSWORD_DID_FAIL: process.env.REACT_APP_LOGIN_FAIL_EVENT_NAME || 'enterPasswordDidFail',
  ENTER_PASSWORD_WAS_ATTEMPTED: process.env.REACT_APP_LOGIN_ATTEMPT_EVENT_NAME || 'enterPasswordWasAttempted',
  
  USER_SUCCESSFULLY_LOGGED_INTO_WAND_WITH_GOOGLE: process.env.REACT_APP_GOOGLE_LOGIN_EVENT_NAME || 'userSuccessfullyLoggedIntoWandWithGoogle',
  USER_SUCCESSFULLY_CREATED_WAND_ACCOUNT_WITH_GOOGLE: process.env.REACT_APP_GOOGLE_SIGNUP_EVENT_NAME || 'userSuccessfullyCreatedWandAccountWithGoogle',
  USER_DID_TAP_GOOGLE_SIGN_IN_BUTTON: process.env.REACT_APP_GOOGLE_TAP_EVENT_NAME || 'userDidTapGoogleSignInButton',
  
  // Generation Events - iOS Aligned
  GENERATE_DID_SUCCEED: process.env.REACT_APP_GENERATE_SUCCESS_EVENT_NAME || 'generateDidSucceed',
  GENERATE_DID_FAIL: process.env.REACT_APP_GENERATE_FAIL_EVENT_NAME || 'generateDidFail',
  GENERATE_WAS_ATTEMPTED: process.env.REACT_APP_GENERATE_ATTEMPT_EVENT_NAME || 'generateWasAttempted',
  
  // Subscription Events - iOS Aligned
  PURCHASE_SUBSCRIPTION_DID_SUCCEED: process.env.REACT_APP_SUB_SUCCESS_EVENT_NAME || 'purchaseSubscriptionDidSucceed',
  PURCHASE_SUBSCRIPTION_DID_FAIL: process.env.REACT_APP_SUB_FAIL_EVENT_NAME || 'purchaseSubscriptionDidFail',
  PURCHASE_CREDITS_DID_SUCCEED: process.env.REACT_APP_CREDITS_SUCCESS_EVENT_NAME || 'purchaseCreditsDidSucceed',
  PURCHASE_CREDITS_DID_FAIL: process.env.REACT_APP_CREDITS_FAIL_EVENT_NAME || 'purchaseCreditsDidFail',
  INCREMENTED_CREDIT_COUNT: process.env.REACT_APP_CREDITS_ADDED_EVENT_NAME || 'incrementedCreditCount',
  DECREMENTED_CREDIT_COUNT: process.env.REACT_APP_CREDITS_USED_EVENT_NAME || 'decrementedCreditCount',
  
  // Keep existing image upload/download events
  IMAGE_UPLOADED: process.env.REACT_APP_IMAGE_UPLOADED_EVENT_NAME || 'Image Uploaded',
  IMAGE_DOWNLOADED: process.env.REACT_APP_IMAGE_DOWNLOADED_EVENT_NAME || 'Image Downloaded',
};

// Base properties that are added to all events
const getBaseEventProperties = () => ({
  platform: 'web',
  timestamp: new Date().toISOString(),
  app_version: process.env.REACT_APP_VERSION || '1.0.0',
});

// Track events with consistent platform tagging
export const trackEvent = (eventName: string, eventProperties?: Record<string, any>) => {
  const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY || '';
  
  if (!apiKey) {
    console.log(`📊 [Analytics Disabled] Would track: ${eventName}`, eventProperties);
    return;
  }

  const finalProperties = {
    ...getBaseEventProperties(),
    ...eventProperties,
  };

  amplitude.track(eventName, finalProperties);
  console.log(`📊 Tracked: ${eventName}`, finalProperties);
};

// Set platform user property as recommended by iOS engineer
export const setPlatformUserProperty = () => {
  setUserProperties({ platform: 'web' });
};

// ========================================
// AUTHENTICATION EVENTS (iOS Aligned)
// ========================================

export const trackCreateAccountAttempted = (authMethod: 'email' | 'phone' | 'google') => {
  trackEvent(EVENT_NAMES.CREATE_ACCOUNT_WAS_ATTEMPTED, { authMethod });
};

export const trackCreateAccountSuccess = (authMethod: 'email' | 'phone' | 'google', userProperties?: Record<string, any>) => {
  trackEvent(EVENT_NAMES.CREATE_ACCOUNT_DID_SUCCEED, {
    authMethod,
    ...userProperties,
  });
};

export const trackCreateAccountFailed = (authMethod: 'email' | 'phone' | 'google', errorMessage?: string) => {
  trackEvent(EVENT_NAMES.CREATE_ACCOUNT_DID_FAIL, {
    authMethod,
    error_message: errorMessage,
  });
};

export const trackPasswordLoginAttempted = (authMethod: 'email' | 'username') => {
  trackEvent(EVENT_NAMES.ENTER_PASSWORD_WAS_ATTEMPTED, { authMethod });
};

export const trackPasswordLoginSuccess = (authMethod: 'email' | 'username') => {
  trackEvent(EVENT_NAMES.ENTER_PASSWORD_DID_SUCCEED, { authMethod });
};

export const trackPasswordLoginFailed = (authMethod: 'email' | 'username', errorMessage?: string) => {
  trackEvent(EVENT_NAMES.ENTER_PASSWORD_DID_FAIL, {
    authMethod,
    error_message: errorMessage,
  });
};

export const trackGoogleSignInButtonTapped = () => {
  trackEvent(EVENT_NAMES.USER_DID_TAP_GOOGLE_SIGN_IN_BUTTON, {});
};

export const trackGoogleLoginSuccess = () => {
  trackEvent(EVENT_NAMES.USER_SUCCESSFULLY_LOGGED_INTO_WAND_WITH_GOOGLE, {
    authMethod: 'google',
  });
};

export const trackGoogleSignupSuccess = () => {
  trackEvent(EVENT_NAMES.USER_SUCCESSFULLY_CREATED_WAND_ACCOUNT_WITH_GOOGLE, {
    authMethod: 'google',
  });
};

// ========================================
// GENERATION EVENTS (iOS Aligned)
// ========================================

// Enhanced generation tracking to match iOS GenerationProperties structure
export const trackGenerateAttempted = (generationData: {
  prompt?: string;
  foundationModel?: string;
  mode?: string;
  isFinetunedModel?: boolean;
  isImageIncluded?: boolean;
  style?: string;
}) => {
  trackEvent(EVENT_NAMES.GENERATE_WAS_ATTEMPTED, {
    prompt: generationData.prompt || '',
    foundationModelName: generationData.foundationModel || 'FLUX',
    mode: generationData.mode || 'sketch_to_image',
    isFinetunedModel: generationData.isFinetunedModel || false,
    isImageIncluded: generationData.isImageIncluded || true,
    style: generationData.style || '',
  });
};

export const trackGenerateSuccess = (generationData: {
  prompt?: string;
  fileSize?: number;
  fileType?: string;
  processingTimeMs?: number;
  foundationModel?: string;
  mode?: string;
  isFinetunedModel?: boolean;
  isImageIncluded?: boolean;
  style?: string;
}) => {
  trackEvent(EVENT_NAMES.GENERATE_DID_SUCCEED, {
    prompt: generationData.prompt || '',
    file_size_bytes: generationData.fileSize,
    file_type: generationData.fileType,
    processing_time_ms: generationData.processingTimeMs,
    foundationModelName: generationData.foundationModel || 'FLUX',
    mode: generationData.mode || 'sketch_to_image',
    isFinetunedModel: generationData.isFinetunedModel || false,
    isImageIncluded: generationData.isImageIncluded || true,
    style: generationData.style || '',
  });
};

export const trackGenerateFailed = (generationData: {
  prompt?: string;
  errorMessage?: string;
  foundationModel?: string;
  mode?: string;
  isFinetunedModel?: boolean;
  isImageIncluded?: boolean;
  style?: string;
}) => {
  trackEvent(EVENT_NAMES.GENERATE_DID_FAIL, {
    prompt: generationData.prompt || '',
    error_message: generationData.errorMessage,
    foundationModelName: generationData.foundationModel || 'FLUX',
    mode: generationData.mode || 'sketch_to_image',
    isFinetunedModel: generationData.isFinetunedModel || false,
    isImageIncluded: generationData.isImageIncluded || true,
    style: generationData.style || '',
  });
};

// ========================================
// SUBSCRIPTION/CREDITS EVENTS (iOS Aligned)
// ========================================

export const trackPurchaseSubscriptionSuccess = (subscriptionDetails?: Record<string, any>) => {
  trackEvent(EVENT_NAMES.PURCHASE_SUBSCRIPTION_DID_SUCCEED, subscriptionDetails);
};

export const trackPurchaseSubscriptionFailed = (errorMessage?: string) => {
  trackEvent(EVENT_NAMES.PURCHASE_SUBSCRIPTION_DID_FAIL, {
    error_message: errorMessage,
  });
};

export const trackPurchaseCreditsSuccess = (creditDetails?: Record<string, any>) => {
  trackEvent(EVENT_NAMES.PURCHASE_CREDITS_DID_SUCCEED, creditDetails);
};

export const trackPurchaseCreditsFailed = (errorMessage?: string) => {
  trackEvent(EVENT_NAMES.PURCHASE_CREDITS_DID_FAIL, {
    error_message: errorMessage,
  });
};

export const trackCreditsIncremented = (amount: number, reason: string) => {
  trackEvent(EVENT_NAMES.INCREMENTED_CREDIT_COUNT, {
    credit_amount: amount,
    reason, // 'purchase', 'refund', 'bonus', etc.
  });
};

export const trackCreditsDecremented = (amount: number, reason: string) => {
  trackEvent(EVENT_NAMES.DECREMENTED_CREDIT_COUNT, {
    credit_amount: amount,
    reason, // 'generation', 'expired', etc.
  });
};

// ========================================
// EXISTING IMAGE EVENTS (Keep for backward compatibility)
// ========================================

export const trackImageUpload = (fileSize?: number, fileType?: string) => {
  trackEvent(EVENT_NAMES.IMAGE_UPLOADED, {
    file_size_bytes: fileSize,
    file_type: fileType,
  });
};

export const trackImageDownload = () => {
  trackEvent(EVENT_NAMES.IMAGE_DOWNLOADED, {});
};

// ========================================
// LEGACY FUNCTIONS (For backward compatibility)
// ========================================

// Keep these for existing code that might still use them
export const trackCreateAccount = trackCreateAccountSuccess;
export const trackPasswordLogin = trackPasswordLoginSuccess;
export const trackGoogleLogin = trackGoogleLoginSuccess;